<script lang="ts">
  import { createForm } from 'svelte-forms-lib'
  import { type CardProps } from './CardProps'
  import { set_card } from '../Cards/observables'
  import FormGroup from './FormGroup.svelte'
  import * as T from 'io-ts'
  import { withMessage, NonEmptyString } from 'io-ts-types'
  import { fieldErrorsOrUndefined } from '../utilities/forms'
  import { pipe } from 'fp-ts/function'
  import { fold } from 'fp-ts/lib/Either'
  import { CardT } from '../shared/codecs/Card'
  import CtaButton from '../Generic/CtaButton.svelte'
  import { goto } from '@roxi/routify'

  export let initialValues: CardProps & { id?: string } = {
    id: undefined,
    heading: '' as NonEmptyString,
    red_text: '' as NonEmptyString,
    amber_text: '' as NonEmptyString,
    green_text: '' as NonEmptyString,
  }
  let message = ''
  const updating = !!initialValues.id
  const error_message = updating ? 'Error while updating this card.' : 'Unable to add this card'
  const success_message = updating ? 'Card Updated' : 'Added Card'
  const FormT = T.type({
    heading: withMessage(NonEmptyString, () => 'A heading is required'),
    green_text: withMessage(NonEmptyString, () => 'Text for the Green light is required'),
    amber_text: withMessage(NonEmptyString, () => 'Text for the Amber light is required'),
    red_text: withMessage(NonEmptyString, () => 'Text for the Red light is required'),
  })

  const { form, errors, touched, isValid, isSubmitting, handleChange, handleSubmit } = createForm({
    initialValues,
    validate: values => fieldErrorsOrUndefined(T.partial(FormT.props), values),
    onSubmit: async (values: T.TypeOf<typeof FormT>) => {
      const card = pipe(
        FormT.decode(values),
        fold(
          () => {
            throw new Error('Failed to decode the card after validation.')
          },
          result => CardT.encode({ ...result, deleted: false }),
        ),
      )
      try {
        await $set_card({
          heading: card.heading as NonEmptyString,
          green_text: card.green_text as NonEmptyString,
          amber_text: card.amber_text as NonEmptyString,
          red_text: card.red_text as NonEmptyString,
          id: initialValues.id,
        })
        message = success_message
      } catch (err) {
        if (err instanceof Error) {
          console.error(err.message)
        }
        console.error(error_message)
        message = error_message
      }
    },
  })

  touched.subscribe(() => (message = ''))
</script>

<div id="container">
  <form on:submit={handleSubmit}>
    <div class="grid">
      <FormGroup key="heading" label="Heading" {form} {errors} {handleChange} />
      <FormGroup key="green_text" label="Green Light Text" {form} {errors} {handleChange} />
      <FormGroup key="amber_text" label="Amber Light Text" {form} {errors} {handleChange} />
      <FormGroup key="red_text" label="Red Light Text" {form} {errors} {handleChange} />
      <div id="submit-buttons">
        <CtaButton disabled={$isSubmitting} kind="negative" on:click={() => void $goto('/cards/index')}>
          Back
        </CtaButton>
        <CtaButton type="submit" disabled={!$isValid || $isSubmitting}>
          {updating ? `Update Card` : 'Add Card'}
        </CtaButton>
      </div>
      {#if message}
        <h1>{message}</h1>
      {/if}
    </div>
  </form>
</div>

<style>
  .grid {
    padding: 2em;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
  }

  #submit-buttons {
    grid-column: 1 / span 3;
    display: flex;
    justify-content: space-evenly;
    margin: 2em 0em;
  }

  h1 {
    grid-column: 1 / span 3;
  }

  #container {
    width: 100%;
    max-width: 75em;
  }
</style>
